/**
 * Per-agency vocabulary.
 *
 * One agency says "tenant", another says "client", a third says "occupant"; a shopping-plaza
 * manager lets "shops", not "units". Forcing our nouns on them makes the product feel foreign
 * on day one, and every screen inherits the friction.
 *
 * So nouns are configurable, and every label in the UI comes from here. The default set matches
 * what the primary user — the estate manager from the brief — actually says.
 */

export interface Vocabulary {
  tenant: string;
  tenants: string;
  landlord: string;
  landlords: string;
  property: string;
  properties: string;
  unit: string;
  units: string;
  lease: string;
  leases: string;
  rent: string;
  invoice: string;
  invoices: string;
}

export const DEFAULT_VOCABULARY: Vocabulary = {
  tenant: "tenant",
  tenants: "tenants",
  landlord: "landlord",
  landlords: "landlords",
  property: "property",
  properties: "properties",
  unit: "unit",
  units: "units",
  lease: "tenancy",
  leases: "tenancies",
  rent: "rent",
  invoice: "invoice",
  invoices: "invoices",
};

/** Ready-made sets, so an agency picks one in setup instead of typing twelve words. */
export const VOCABULARY_PRESETS: Array<{ name: string; description: string; vocabulary: Vocabulary }> = [
  {
    name: "Residential lettings",
    description: "Tenants, landlords, properties and tenancies.",
    vocabulary: DEFAULT_VOCABULARY,
  },
  {
    name: "Client-facing agency",
    description: "Clients rather than tenants — for agencies whose relationship is with the payer.",
    vocabulary: { ...DEFAULT_VOCABULARY, tenant: "client", tenants: "clients" },
  },
  {
    name: "Commercial and retail",
    description: "Shops and units inside a plaza or complex, let on leases.",
    vocabulary: {
      ...DEFAULT_VOCABULARY,
      tenant: "occupant",
      tenants: "occupants",
      property: "complex",
      properties: "complexes",
      unit: "shop",
      units: "shops",
      lease: "lease",
      leases: "leases",
    },
  },
  {
    name: "Estate management",
    description: "Estates made up of houses, with residents paying dues.",
    vocabulary: {
      ...DEFAULT_VOCABULARY,
      tenant: "resident",
      tenants: "residents",
      property: "estate",
      properties: "estates",
      unit: "house",
      units: "houses",
    },
  },
];

export function vocabulary(overrides?: Partial<Vocabulary> | null): Vocabulary {
  return { ...DEFAULT_VOCABULARY, ...(overrides ?? {}) };
}

/** `Tenant` from `tenant` — used wherever a label starts a sentence or heads a column. */
export function title(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Substitute vocabulary into a fixed string: `t("Add a {unit}")` gives "Add a shop" for a
 * plaza manager. Keeps the source strings readable, which a lookup-key scheme would not.
 */
export function phrase(template: string, vocab: Vocabulary): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const lower = key.toLowerCase();
    const value = (vocab as unknown as Record<string, string>)[lower];
    if (!value) return match;
    return key[0] === key[0].toUpperCase() ? title(value) : value;
  });
}

/**
 * The nouns an agency can choose, one at a time.
 *
 * Presets were the only way to change these, and a preset is an all-or-nothing swap: an agency
 * that manages an estate of houses *and* a parade of shops had to pick whichever preset was least
 * wrong. Choosing each noun separately is what the job actually needs.
 *
 * Singular and plural travel together. Letting them be set independently produces "estate /
 * shops" the first time somebody edits one box and not the other, and every screen inherits it.
 */
export interface VocabularyTerm {
  /** The singular key on `Vocabulary`. */
  key: keyof Vocabulary;
  /** The plural key, where the noun has one. */
  pluralKey?: keyof Vocabulary;
  /** What this noun means, in words the customer would use. */
  label: string;
  hint: string;
  options: Array<{ one: string; many?: string }>;
}

export const VOCABULARY_TERMS: VocabularyTerm[] = [
  {
    key: "tenant",
    pluralKey: "tenants",
    label: "The people who pay",
    hint: "Whoever occupies the unit and owes the money.",
    options: [
      { one: "tenant", many: "tenants" },
      { one: "resident", many: "residents" },
      { one: "occupant", many: "occupants" },
      { one: "client", many: "clients" },
      { one: "lessee", many: "lessees" },
    ],
  },
  {
    key: "landlord",
    pluralKey: "landlords",
    label: "The people who own",
    hint: "Whoever the money is remitted to.",
    options: [
      { one: "landlord", many: "landlords" },
      { one: "owner", many: "owners" },
      { one: "client", many: "clients" },
      { one: "lessor", many: "lessors" },
    ],
  },
  {
    key: "property",
    pluralKey: "properties",
    label: "The building or site",
    hint: "The thing that has an address.",
    options: [
      { one: "property", many: "properties" },
      { one: "estate", many: "estates" },
      { one: "building", many: "buildings" },
      { one: "complex", many: "complexes" },
      { one: "plaza", many: "plazas" },
      { one: "development", many: "developments" },
    ],
  },
  {
    key: "unit",
    pluralKey: "units",
    label: "What is let",
    hint: "The lettable thing inside it — one tenancy, one of these.",
    options: [
      { one: "unit", many: "units" },
      { one: "flat", many: "flats" },
      { one: "apartment", many: "apartments" },
      { one: "house", many: "houses" },
      { one: "shop", many: "shops" },
      { one: "office", many: "offices" },
      { one: "space", many: "spaces" },
      { one: "plot", many: "plots" },
    ],
  },
  {
    key: "lease",
    pluralKey: "leases",
    label: "The agreement",
    hint: "What is signed for a term.",
    options: [
      { one: "tenancy", many: "tenancies" },
      { one: "lease", many: "leases" },
      { one: "agreement", many: "agreements" },
      { one: "contract", many: "contracts" },
    ],
  },
  {
    key: "rent",
    label: "What is charged for it",
    hint: "The recurring amount.",
    options: [{ one: "rent" }, { one: "levy" }, { one: "dues" }, { one: "service fee" }, { one: "licence fee" }],
  },
  {
    key: "invoice",
    pluralKey: "invoices",
    label: "The demand for payment",
    hint: "What goes out when money falls due.",
    options: [
      { one: "invoice", many: "invoices" },
      { one: "bill", many: "bills" },
      { one: "demand", many: "demands" },
      { one: "statement", many: "statements" },
    ],
  },
];

/**
 * Set one noun and its plural together.
 *
 * Passing a plural is optional: for the nouns that have no natural one — "rent" — there is
 * nothing to keep in step.
 */
export function withTerm(current: Vocabulary, term: VocabularyTerm, one: string, many?: string): Vocabulary {
  const next: Vocabulary = { ...current, [term.key]: one.trim() || current[term.key] };
  if (term.pluralKey) {
    const plural = (many ?? "").trim() || defaultPlural(one);
    next[term.pluralKey] = plural;
  }
  return next;
}

/**
 * A serviceable plural for a word somebody typed themselves.
 *
 * Deliberately simple. English plurals cannot be derived reliably, and a wrong guess is visible
 * on every screen — so the interface shows the result and lets it be corrected, rather than
 * pretending this is solved.
 */
export function defaultPlural(word: string): string {
  const trimmed = word.trim();
  if (!trimmed) return trimmed;
  if (/(s|x|z|ch|sh)$/i.test(trimmed)) return `${trimmed}es`;
  if (/[^aeiou]y$/i.test(trimmed)) return `${trimmed.slice(0, -1)}ies`;
  return `${trimmed}s`;
}
